import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    input,
    OnDestroy,
    OnInit,
    output
} from '@angular/core';
import {
    BaseOrder,
    constants,
    DocumentFile,
    DocumentViewerTabTypes,
    SignatureField
} from '@trade-platform/ui-shared';
import { commentStatus, commentTag, fileType, orderStatus } from '@trade-platform/lib-enums';
import { Subscription } from 'rxjs';
import { ProfileStoreFacade } from '@advisor-ui/app-services';
import {
    getDocumentIndexById,
    getDocumentRouteId,
    getFileRouteId,
    getOrderStatusIndex
} from '../../../utils';
import { AsyncPipe, NgClass } from '@angular/common';
import { LetDirective } from '@ngrx/component';
import {
    AixDataTestingDirective,
    AixHeaderSectionComponent,
    AixSlideToggleComponent
} from '@trade-platform/ui-components';
import { AixDocumentListComponent } from '../document-list/document-list';
import { AixOrderFormCommentsComponent } from '../../../order-comments/order-form-comments/order-form-comments';
import { OrderFormCommentsPipe } from '../../../order-comments/order-form-comments-pipe';
import { BaseOrdersStoreFacade, ORDERS_STORE_FACADE } from '../../../base.orders.store.facade';
import { getFilesToDisplay } from '../../document-viewer/document-viewer.helper';

const flatten = (array: any[]): any[] => {
    return array.reduce((res: any[], curr) => {
        if (curr.children) {
            return res.concat(curr).concat(flatten(curr.children));
        } else {
            return res.concat(curr);
        }
    }, []);
};

@Component({
    selector: 'aix-document-tabs',
    templateUrl: 'document-tabs.html',
    styleUrls: ['./document-tabs.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        LetDirective,
        NgClass,
        AixHeaderSectionComponent,
        AixDataTestingDirective,
        AixDocumentListComponent,
        AixOrderFormCommentsComponent,
        AixSlideToggleComponent,
        AsyncPipe,
        OrderFormCommentsPipe
    ]
})
export class AixDocumentTabsComponent implements OnInit, OnDestroy {
    private ref = inject(ChangeDetectorRef);
    storeFacade = inject<BaseOrdersStoreFacade>(ORDERS_STORE_FACADE);
    profileStoreFacade = inject(ProfileStoreFacade);

    // Signature inputs
    signaturesPreview = input<boolean>(false);
    availableRoles = input<
        { groupKey: string; role: string; displayName: string; color: string; isWetSign: boolean }[]
    >([]);
    signaturesForRole = input<Map<string, SignatureField[]>>(new Map());
    hiddenRoles = input<Set<string>>(new Set());
    roleFieldIndex = input<Map<string, number>>(new Map());

    onNotOnboarded = output<string>();
    onDownloadDocument = output<string>();
    onToggleRole = output<string>();
    onPrevSignature = output<string>();
    onNextSignature = output<string>();

    signaturesCollapsed = true;
    tabs: DocumentViewerTabTypes[] = ['Order'];
    subscriptions: Subscription[] = [];

    filesToDisplay: DocumentFile[] = [];
    filesToDisplayIds: string[] = [];
    requiredDocuments: DocumentFile[] = [];
    supplementalFiles: DocumentFile[] = [];
    roles: string[];

    readonly commentsMode = 'modify';

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    ngOnInit() {
        const profile = this.profileStoreFacade.profile;
        this.roles = profile ? profile.roles.map(role => role.name) : [];

        const orderId = this.storeFacade.order.id;
        this.storeFacade.actions.getFormComments.dispatch({ data: { orderId: orderId } });

        this.subscriptions.push(
            this.storeFacade.orderSuccess$.subscribe(order => {
                const currentDoc = this.storeFacade.documentViewer.currentDocument;
                this.filesToDisplay = getFilesToDisplay(order);
                this.filesToDisplayIds = this.filesToDisplay.map(
                    (f: DocumentFile) => f.formId || f.id
                );
                this.requiredDocuments = this.filesToDisplay.filter(
                    f => f.type === fileType.form || f.type === fileType.unassignedForm
                );
                this.supplementalFiles = this.filesToDisplay.filter(
                    f =>
                        f.type === fileType.supplemental ||
                        f.type === fileType.unassignedSupplemental
                );
                this.activateFile(order, currentDoc);
                this.ref.detectChanges();
            }),
            this.storeFacade.orderFormCommentsSuccess$.subscribe(_ => {
                this.setCommentsTab();
                this.ref.detectChanges();
            })
        );
    }

    activateFile(order: BaseOrder, docId: string | null) {
        const hasFilesToDisplay = this.filesToDisplay && this.filesToDisplay.length > 0;
        const index = getDocumentIndexById(this.filesToDisplay, docId);

        if (hasFilesToDisplay) {
            this.storeFacade.actions.documentViewerSetFiles.dispatch({
                filesToDisplay: this.filesToDisplay
            });
        }

        /*
          `index` is -1 when `docId` is missing or names a file that is no longer on the order —
          files are replaced rather than updated, so a page loaded before a replacement holds an
          id that has since been soft deleted. Fall back to the first document and report the
          fallback id, so the stale id does not stay in the url.
          refs trade-platform/trade-platform#7593
        */
        const currentFile = index >= 0 ? this.filesToDisplay[index] : undefined;

        if (currentFile) {
            this.storeFacade.actions.documentViewerSetFile.dispatch({ currentFile });

            const form = order.forms[currentFile.formId as string];
            const isOnboarded = form ? form.isOnboarded : true;
            if (
                !isOnboarded &&
                getOrderStatusIndex(order.status) < getOrderStatusIndex(orderStatus.readyToSend)
            ) {
                this.onNotOnboarded.emit(form?.id ?? '');
            }
        } else if (hasFilesToDisplay) {
            this.storeFacade.actions.documentViewerSetFile.dispatch({
                currentFile: this.filesToDisplay[0]
            });
        }

        // Settle on the stable identity rather than echoing whichever one the url carried.
        const resolvedDocumentId = currentFile
            ? getFileRouteId(currentFile)
            : getDocumentRouteId(this.filesToDisplay);
        this.storeFacade.actions.documentViewerSetDocument.dispatch({
            currentDocument: resolvedDocumentId ?? ''
        });
        this.ref.markForCheck();
    }

    setCommentsTab() {
        // Comments;
        const orderFormComments = this.storeFacade.orderFormComments;
        const documentViewAction = this.storeFacade.documentViewer.action;

        if (documentViewAction !== 'none' || (orderFormComments && orderFormComments.length > 0)) {
            if (this.tabs.indexOf('Comments') === -1) {
                this.tabs.push('Comments');

                // Select comments tab if the last message for a reviewer it's a clarification resolved
                if (this.roles.includes(constants.profileTypes.REVIEWER)) {
                    const lastComment = flatten(orderFormComments).sort(
                        (a: number, b: number) => new Date(a).getTime() - new Date(b).getTime()
                    )[0];
                    if (
                        lastComment &&
                        lastComment.commentTag === commentTag.clarification &&
                        lastComment.status === commentStatus.resolved
                    ) {
                        this.onClickTab('Comments');
                    }
                }
            }
        }
    }

    onClickTab(tab: DocumentViewerTabTypes) {
        this.storeFacade.actions.documentViewerSetTab.dispatch({ tab: tab });
    }

    downloadDocument(fileId: string) {
        this.onDownloadDocument.emit(fileId);
    }

    toggleSignaturesCollapsed() {
        this.signaturesCollapsed = !this.signaturesCollapsed;
    }

    getRoleFieldIndex(role: string): number {
        return this.roleFieldIndex()?.get(role) ?? 0;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
